import { Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/protected-route";
import { type Role } from "@/store/auth";

// Pages
import HomePage from "@/pages/HomePage";
import SignInPage from "@/pages/auth/SignInPage";
import PublicLayout from "@/components/layout/public-layout";
import PublicRoute from "@/components/public-route";
import RegisterPage from "@/pages/auth/Register";
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage";
import VerifyPage from "@/pages/VerifyPage";
import VerifySuccessPage from "@/pages/VerifySuccessPage";
import DashboardPage from "@/pages/dashboard/DashboardPage";
import MapPage from "./pages/dashboard/MapPage";
import DashboardLayout from "@/components/layout/dashboard-layout";
import OrganisationsPage from "./pages/dashboard/OrganisationsPage";
import ProjectsPage from "./pages/dashboard/ProjectsPage";
import SettingsPage from "./pages/dashboard/SettingsPage";
import HelpPage from "./pages/dashboard/HelpPage";
import EditOrganisationPage from "./pages/dashboard/EditOrganisationPage";
import AddProjectsPage from "./pages/dashboard/AddProjectsPage";
import EditProjectPage from "./pages/dashboard/EditProjectPage";
import UsersPage from "./pages/dashboard/UsersPage";
import SettingsPageNoEntity from "./pages/dashboard/SettingsPageNoEntity";
import AddCategoryPage from "./pages/dashboard/AddCategoryDataPage";
import DataPage from "./pages/dashboard/DataPage";
import EditCategoryView from "./pages/dashboard/EditCategoryDataPage";
import AddOrganisationPage from "./pages/dashboard/AddOrganisationPage";
import InquiryPage from "./pages/dashboard/InquiryPage";
import ReplyInquiryPage from "./pages/dashboard/ReplyInquiryPage";
import OpportunitiesPage from "./pages/dashboard/OpportunitiesPage";
import AddOpportunities from "./pages/dashboard/AddOpportunities";
import EditOpportunities from "./pages/dashboard/EditOpportunities";
import OpportunityDetailPage from "./pages/dashboard/OpportunityDetailPage";
import SignifyInterestPage from "./pages/dashboard/SignifyInterestPage";
import AppliedOpportunitiesPage from "./pages/dashboard/AppliedOpportunitiesPage";
import OpportunityApplicationDetailPage from "./pages/dashboard/OpportunityApplicationDetailPage";

interface ProtectedRoute {
  path: string;
  element: React.ReactNode;
  roles: Role[];
}

const protectedRoutes: ProtectedRoute[] = [
  {
    path: "/dashboard",
    element: <DashboardPage />,
    roles: ["org_admin", "org_user", "workspace_admin", "workspace_user"],
  },
  {
    path: "/dashboard/map",
    element: <MapPage />,
    roles: ["org_admin", "org_user", "workspace_admin", "workspace_user"],
  },
  {
    path: "/dashboard/organisations",
    element: <OrganisationsPage />,
    roles: ["org_admin", "org_user", "workspace_admin", "workspace_user"],
  },
  {
    path: "/dashboard/organisations/add",
    element: <AddOrganisationPage />,
    roles: ["workspace_admin", "workspace_user"],
  },
  {
    path: "/dashboard/projects",
    element: <ProjectsPage />,
    roles: ["org_admin", "org_user", "workspace_admin", "workspace_user"],
  },
  {
    path: "/dashboard/projects/add",
    element: <AddProjectsPage />,
    roles: ["org_admin", "org_user"],
  },
  {
    path: "/dashboard/projects/:id/edit",
    element: <EditProjectPage />,
    roles: ["org_admin", "org_user"],
  },
  {
    path: "/dashboard/data",
    element: <DataPage />,
    roles: ["workspace_admin", "workspace_user"],
  },
  {
    path: "/dashboard/data/add",
    element: <AddCategoryPage />,
    roles: ["workspace_admin", "workspace_user"],
  },
  {
    path: "/dashboard/data/:id/edit",
    element: <EditCategoryView />,
    roles: ["workspace_admin", "workspace_user"],
  },
  {
    path: "/dashboard/settings",
    element: <SettingsPage />,
    roles: ["org_admin", "org_user", "workspace_admin", "workspace_user"],
  },
  {
    path: "/dashboard/settings/edit-organisation",
    element: <EditOrganisationPage />,
    roles: ["org_admin", "org_user"],
  },
  {
    path: "/dashboard/help",
    element: <HelpPage />,
    roles: ["org_admin", "org_user"],
  },
  {
    path: "/dashboard/inquiries",
    element: <InquiryPage />,
    roles: ["workspace_admin", "workspace_user"],
  },
  {
    path: "/dashboard/inquiries/:id/reply",
    element: <ReplyInquiryPage />,
    roles: ["workspace_admin", "workspace_user"],
  },
  {
    path: "/dashboard/users",
    element: <UsersPage />,
    roles: ["org_admin", "org_user", "workspace_admin", "workspace_user"],
  },
  {
    path: "/dashboard/opportunities",
    element: <OpportunitiesPage />,
    roles: ["org_admin", "org_user", "workspace_admin", "workspace_user"],
  },
  {
    path: "/dashboard/add-opportunities",
    element: <AddOpportunities />,
    roles: ["org_admin", "org_user", "workspace_admin", "workspace_user"],
  },
  {
    path: "/dashboard/opportunities/:id/edit",
    element: <EditOpportunities />,
    roles: ["org_admin", "org_user", "workspace_admin", "workspace_user"],
  },
  {
    path: "/dashboard/opportunities/:id/applications",
    element: <AppliedOpportunitiesPage />,
    roles: ["org_admin", "org_user", "workspace_admin", "workspace_user"],
  },
  {
    path: "/dashboard/opportunities/:id/applications/:applicationId",
    element: <OpportunityApplicationDetailPage />,
    roles: ["org_admin", "org_user", "workspace_admin", "workspace_user"],
  },
  {
    path: "/dashboard/opportunities/:id",
    element: <OpportunityDetailPage />,
    roles: ["org_admin", "org_user", "workspace_admin", "workspace_user"],
  },
  {
    path: "/dashboard/signify-interest/:id",
    element: <SignifyInterestPage />,
    roles: ["org_admin", "org_user", "workspace_user"],
  },
];

function App() {
  return (
    <div className="">
      <Routes>
        {/* Public Routes */}
        <Route
          path="/"
          element={
            <PublicRoute>
              <PublicLayout />
            </PublicRoute>
          }
        >
          <Route index element={<HomePage />} />
          <Route path="/sign-in" element={<SignInPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
        </Route>

        {/* Pages for verification of Organisation */}
        {/* verify routes */}
        <Route
          element={<ProtectedRoute allowedRoles={["org_admin", "org_user"]} />}
        >
          <Route path="/verify" element={<VerifyPage />} />
          <Route path="/verify/success" element={<VerifySuccessPage />} />
        </Route>

        {/* Moving out settings to allow users without organisation or workspace to access it */}
        <Route element={<ProtectedRoute />}>
          <Route path="/settings" element={<SettingsPageNoEntity />} />
        </Route>

        {/* Protected Dashboard Routes */}
        <Route element={<DashboardLayout defaultOpen={true} />}>
          {protectedRoutes.map(({ path, element, roles }) => (
            <Route key={path} element={<ProtectedRoute allowedRoles={roles} />}>
              <Route path={path} element={element} />
            </Route>
          ))}
        </Route>
      </Routes>
      <Toaster position="top-center" />
    </div>
  );
}

export default App;
